import { useCallback, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import useAxisLockedSwipe from "@/lib/useAxisLockedSwipe";

/**
 * HeroCarousel — swipeable media carousel (photos + videos) on a property page.
 * `items` is a list of { type: "image" | "video", src, embed, thumb, provider }
 * produced by buildMediaList(). Videos use a play-overlay poster on mobile —
 * tapping expands to the Photo Explorer which plays them inline.
 *
 * Mobile uses a transform-based slider with axis-locked touch handling so a
 * vertical scroll attempt while the finger lands on a photo always lets the
 * page scroll (the hero is a 60vh block — vertical scroll past it is the
 * single most common gesture on this page).
 */
export default function HeroCarousel({ items, propertyName, onExpand }) {
  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(0);
  const wrapRef = useRef(null);

  const media = (items || []).slice(0, 15);
  const N = media.length;

  const goTo = useCallback((i) => {
    if (!N) return;
    setActive(((i % N) + N) % N);
  }, [N]);

  useAxisLockedSwipe({
    elementRef: wrapRef,
    onSwipe: (dir) => setActive((cur) => (((cur + dir) % N) + N) % N),
    onDrag: setDrag,
  });

  const renderPoster = (m, i) => {
    if (m.type === "image") {
      return <img src={m.src} alt={`${propertyName} ${i + 1}`} draggable={false} className="h-full w-full object-cover" loading={i === 0 ? "eager" : "lazy"} />;
    }
    return (
      <>
        {m.thumb ? (
          <img src={m.thumb} alt={`${propertyName} video ${i + 1}`} draggable={false} className="h-full w-full object-cover" loading="lazy" />
        ) : m.provider === "file" ? (
          <video src={m.src} className="h-full w-full object-cover" preload="metadata" muted playsInline />
        ) : (
          <div className="h-full w-full bg-noir/80" />
        )}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-parchment/95 text-forest shadow-soft">
            <Play size={22} className="translate-x-0.5 fill-current" />
          </span>
        </span>
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-parchment">Video</span>
      </>
    );
  };

  return (
    <>
      {/* Mobile: swipeable full-bleed carousel — transform-based, vertical-page-scroll-safe */}
      <div ref={wrapRef} className="relative h-[60vh] overflow-hidden md:hidden">
        <div
          className="flex h-full w-full"
          data-testid="mobile-hero-carousel"
          style={{
            transform: `translate3d(calc(${-active * 100}% + ${drag}px), 0, 0)`,
            transition: drag === 0 ? "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
          }}
        >
          {media.map((m, i) => (
            <button
              key={`${m.url || m.src || i}-${i}`}
              onClick={() => onExpand?.(i)}
              data-testid={`hero-slide-${i}`}
              data-media-type={m.type}
              className="relative h-full w-full shrink-0"
              aria-label={m.type === "video" ? `Play video ${i + 1}` : `Photo ${i + 1}`}
            >
              {renderPoster(m, i)}
            </button>
          ))}
        </div>
        <div className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/40 px-3 py-1.5 backdrop-blur">
          <span className="text-xs text-white">{active + 1} / {N}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); goTo(active - 1); }} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur" aria-label="Previous"><ChevronLeft size={18}/></button>
        <button onClick={(e) => { e.stopPropagation(); goTo(active + 1); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur" aria-label="Next"><ChevronRight size={18}/></button>
      </div>

      {/* Desktop: editorial mosaic — videos render with play overlay; click expands explorer */}
      <div className="hidden md:block">
        <div className="gallery-mosaic">
          {media.slice(0, 5).map((m, i) => (
            <button key={`${m.url || m.src || i}-${i}`} onClick={() => onExpand?.(i)} data-testid={`gallery-tile-${i}`} data-media-type={m.type}
              className="relative overflow-hidden rounded-xl">
              <div className="h-full w-full transition-transform duration-700 hover:scale-105">
                {renderPoster(m, i)}
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
